import React from 'react';
import { Badge, Box, Button, ButtonBase, Chip, Skeleton, Typography } from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { SupportCenterHeaderBadge } from '../SupportCenterIcon';
import type { AssistantConversationSummary } from '../../../services/assistantCentral';
import { conversationListTitle } from '../assistant/assistantConversationFormat';
import MessengerHeader from './MessengerHeader';
import { MESSENGER, formatMessengerRelativeTime, messengerListRowSx, messengerSubtitleSx } from './messengerTheme';

interface MessengerMessagesProps {
  conversations: AssistantConversationSummary[] | null;
  unreadIds: Set<string>;
  onClose: () => void;
  onOpenConversation: (id: string) => void;
  onNewMessage: () => void;
}

const MessengerMessages: React.FC<MessengerMessagesProps> = ({
  conversations,
  unreadIds,
  onClose,
  onOpenConversation,
  onNewMessage,
}) => (
  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative', bgcolor: MESSENGER.surface }}>
    <MessengerHeader title="Mensagens" onClose={onClose} />
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pb: 10 }}>
      {conversations === null &&
        [0, 1, 2].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.5 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" />
              <Skeleton width="85%" />
            </Box>
          </Box>
        ))}
      {conversations?.length === 0 && (
        <Box sx={{ textAlign: 'center', px: 4, pt: 8, color: MESSENGER.slate }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: MESSENGER.slateFaint, mb: 1 }} />
          <Typography sx={{ fontWeight: 700, color: MESSENGER.navy, fontSize: '0.92rem' }}>Nenhuma conversa ainda</Typography>
          <Typography sx={{ ...messengerSubtitleSx, mt: 0.5 }}>Suas conversas com a IA e com a equipe Klinvia aparecem aqui.</Typography>
        </Box>
      )}
      {conversations?.map((c) => {
        const unread = unreadIds.has(c.id);
        const withTeam = c.status === 'escalated';
        return (
          <ButtonBase key={c.id} onClick={() => onOpenConversation(c.id)} sx={messengerListRowSx}>
            <Badge color="error" variant="dot" invisible={!unread} overlap="circular">
              <SupportCenterHeaderBadge size={36} />
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography noWrap sx={{ flex: 1, fontSize: '0.86rem', color: MESSENGER.navy, fontWeight: unread ? 700 : 500 }}>
                  {conversationListTitle(c)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: MESSENGER.slateFaint, flexShrink: 0 }}>
                  {formatMessengerRelativeTime(c.last_message_at)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {withTeam && (
                  <Chip
                    size="small"
                    label="Com a equipe"
                    sx={{ height: 18, fontSize: '0.64rem', fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.14)', color: '#B45309' }}
                  />
                )}
                <Typography noWrap sx={{ ...messengerSubtitleSx, flex: 1, minWidth: 0 }}>
                  {c.last_message_role === 'user' && c.last_message_preview ? `Você: ${c.last_message_preview}` : c.last_message_preview || '—'}
                </Typography>
              </Box>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
    <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 16, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <Button
        variant="contained"
        disableElevation
        endIcon={<SendRoundedIcon />}
        onClick={onNewMessage}
        sx={{
          pointerEvents: 'auto',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.86rem',
          borderRadius: '10px',
          px: 2.25,
          py: 1,
          bgcolor: MESSENGER.teal,
          boxShadow: '0 6px 18px rgba(13, 148, 136, 0.3)',
          '&:hover': { bgcolor: MESSENGER.tealDark },
        }}
      >
        Envie uma mensagem
      </Button>
    </Box>
  </Box>
);

export default MessengerMessages;
